import { useEffect, useState } from 'react'
import type { Lead } from '@/types/crm'
import { normaliseWebsite } from '../normalise'
import type { LeadProfilePatch } from '../repository/leadsRepository'

/**
 * The company's own details, editable where they are read.
 *
 * ## What this replaced
 *
 * A modal called "Edit lead info", opened from a five-pixel `Edit` link in the
 * middle of a row of meta text. Seven fields lived behind it, and the only way
 * to correct a website was to find the link, wait for a dialog, retype, save,
 * and watch the dialog close over the lead you were reading. A rep working a
 * list does that dozens of times a day.
 *
 * They are inputs now, in the panel, with the value in them. Editing a field is
 * clicking in it.
 *
 * ## Saving on blur, and why not on every keystroke
 *
 * A write per character would be a write per character — hundreds of Firestore
 * updates to type a domain, each one recomputing `contactDomains` and each one
 * a chance for the lead to re-render under the cursor. Blur is the moment the
 * value is finished, and Tab between fields commits each one on the way past.
 *
 * Nothing is written when nothing changed, so tabbing through a form reading it
 * writes nothing at all.
 *
 * ## Why the whole patch goes every time
 *
 * `updateLeadProfile` takes a complete `LeadProfilePatch` and recomputes
 * `contactDomains` from the website and the contacts — that is the field the
 * Cloud Functions match product signups on, so it has to be derived from
 * whatever the website *is* now, not from a delta. Sending one field would mean
 * teaching the repository a second, partial path that computes the same thing
 * differently.
 *
 * `stage` rides along unchanged. It is not edited here: the stepper and the next
 * step own the stage, because moving one is an action with a note attached
 * rather than a field with a value.
 *
 * ## The owner is here too, and it writes on its own path
 *
 * It sat in the identity strip above until 4 Sep 2026, where it was the second
 * appearance of a value the OWNER column already showed. Editing belongs in one
 * place, so it moved into the form — but it does **not** ride in the patch:
 * `handleOwnerChange` also writes `ownerEmail`, which is the only field
 * `firestore.rules` trusts to decide whose leads an external rep may see. A
 * display name in a patch cannot set it, and getting that wrong hands somebody
 * else's book over.
 */
export function LeadFields({
  lead,
  owners,
  currentUser,
  onSave,
  onOwnerChange,
}: {
  lead: Lead
  owners: string[]
  currentUser: string
  onSave: (patch: LeadProfilePatch) => Promise<void>
  onOwnerChange: (owner: string) => void
}) {
  /**
   * The draft, seeded once from the lead.
   *
   * Deliberately *not* re-seeded from incoming snapshots: this lead is on a
   * live `onSnapshot`, and copying every update into the boxes would overwrite
   * whatever somebody is halfway through typing — the classic way an autosaving
   * form eats input. The caller mounts this with `key={lead.id}`, so opening a
   * different company gets a fresh form and there is nothing to re-seed.
   */
  const [draft, setDraft] = useState(() => fieldsOf(lead))
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The tick is a reassurance, not a state — it goes away on its own.
  useEffect(() => {
    if (!saved) return
    const t = setTimeout(() => setSaved(null), 1600)
    return () => clearTimeout(t)
  }, [saved])

  const commit = async (key: keyof ReturnType<typeof fieldsOf>) => {
    const before = fieldsOf(lead)
    if (draft[key] === before[key]) return
    setSaving(key)
    setError(null)
    try {
      await onSave({
        name: draft.name.trim() || lead.name,
        region: draft.region,
        country: draft.country,
        address: draft.address,
        website: draft.website,
        instagram: draft.instagram,
        facebook: draft.facebook,
        // Not edited here — see the note above.
        stage: lead.stage,
        seatsExpected: draft.seats.trim() === '' ? 0 : Number(draft.seats),
      })
      setSaved(key)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
      // Put the stored value back, so the box never shows something that is
      // not in Firestore.
      setDraft(fieldsOf(lead))
    } finally {
      setSaving(null)
    }
  }

  const field = (
    key: keyof ReturnType<typeof fieldsOf>,
    label: string,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
  ) => (
    <label className="leadfields__field">
      <span className="leadfields__label">
        {label}
        {saving === key && <span className="leadfields__state">saving…</span>}
        {saved === key && <span className="leadfields__state leadfields__state--ok">saved</span>}
      </span>
      <input
        className="input"
        value={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        onBlur={() => void commit(key)}
        // Enter commits without leaving the field, for anybody who types a
        // value and expects it to stick before they look away.
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit(key)
          if (e.key === 'Escape') setDraft(fieldsOf(lead))
        }}
        {...props}
      />
    </label>
  )

  /** Bare host, whatever the field holds — see the note on the links below. */
  const host = normaliseWebsite(lead.website)

  return (
    <section className="card leadfields">
      {/* No card label: the panel has one card of inputs and the labels on the
          boxes say what it is. A heading over it repeated the obvious. */}
      {error && <div className="modal__error">{error}</div>}

      {/*
        Three groups, in this order, because the previous order was none.

        **The company and where it is** first, as an address reads — name,
        street, region, country. **Who has it and how big it is** next: the two
        fields a rep actually changes while working the lead. **Where it is on
        the web** last, with the links to those same places directly underneath.
      */}
      <div className="leadfields__grid">
        {field('name', 'Company name')}
        {field('address', 'Street address')}
        {field('region', 'Region', { placeholder: 'Lofoten' })}
        {field('country', 'Country', { placeholder: 'Norway' })}

        {/* Its own control, not one of the text boxes — see the note above on
            why the owner does not travel in the patch. */}
        <label className="leadfields__field">
          <span className="leadfields__label">
            Owner
            {currentUser && lead.owner !== currentUser && (
              <button className="linkbtn" onClick={() => onOwnerChange(currentUser)}>
                Assign to me
              </button>
            )}
          </span>
          <select
            value={lead.owner}
            onChange={(ev) => onOwnerChange(ev.target.value)}
            aria-label="Lead owner"
          >
            {[...new Set([lead.owner, 'Unassigned', currentUser, ...owners])]
              .filter(Boolean)
              .map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
          </select>
        </label>

        {field('seats', 'Seats', { type: 'number', min: 0, step: 1, placeholder: 'How many?' })}

        {field('website', 'Website', { placeholder: 'company.no' })}
        {field('instagram', 'Instagram', { placeholder: '@company' })}
        {field('facebook', 'Facebook', { placeholder: '@company' })}
      </div>

      {/*
        The links, under the boxes that set them.

        They were up in the identity strip, a card away from the fields they
        reflect. A handle and the profile it opens are the same fact, so
        correcting one and clicking the other should not be two places — and
        `normaliseHandle` means what is stored is a handle whichever form was
        pasted in, so these can always be built from it.

        The site runs through `normaliseWebsite` at render rather than trusting
        the stored value. `updateLeadProfile` normalises on save, but 54 of the
        478 leads with a website were imported carrying the scheme —
        "https://alpenverein.it" — and prefixing that with another scheme gave
        `https://https://alpenverein.it`, a link that has been quietly dead for
        as long as it has existed. Normalising here fixes it whatever is stored.
      */}
      {(host || lead.instagram || lead.facebook) && (
        <div className="leadfields__links">
          {host && (
            <a href={`https://${host}`} target="_blank" rel="noreferrer">
              Open site ↗
            </a>
          )}
          {lead.instagram && (
            <a href={`https://instagram.com/${lead.instagram}`} target="_blank" rel="noreferrer">
              Instagram ↗
            </a>
          )}
          {lead.facebook && (
            <a href={`https://facebook.com/${lead.facebook}`} target="_blank" rel="noreferrer">
              Facebook ↗
            </a>
          )}
        </div>
      )}
    </section>
  )
}

/** The editable fields as strings, which is what an input holds. */
function fieldsOf(lead: Lead) {
  return {
    name: lead.name,
    region: lead.region,
    country: lead.country,
    address: lead.address,
    website: lead.website,
    instagram: lead.instagram,
    facebook: lead.facebook,
    // Empty rather than "0", so an unset seat count reads as unset. See
    // `Lead.seatsExpected`.
    seats: lead.seatsExpected ? String(lead.seatsExpected) : '',
  }
}
